"use client";

import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { ClockIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

export interface RentalCarTimesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  dropOffLocationType?: boolean;
  searchBar?: boolean;
}

const timeSlots = Array.from({ length: 24 }, (_, h) => [
  `${h.toString().padStart(2, "0")}:00`,
  `${h.toString().padStart(2, "0")}:30`,
]).flat();

const RentalCarTimesRangeInput: FC<RentalCarTimesRangeInputProps> = ({
  className = "",
  fieldClassName = "p-4",
  hasButtonSubmit = true,
  dropOffLocationType,
  searchBar,
}) => {
  const {
    startDate,

    loadingAvailableFleet,
    setStartDate,
    setEndDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    callAvailableFleetAPI,
    partiallyUnavailableTimes,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
  } = useData();

  const location = useLocation();
  const navigate = useNavigate();

  const shouldBlockTime =
    location.pathname === "/listing-car-detail" ||
    location.pathname === "/car-listing-detail";

  useEffect(() => {
    validateEndTime();
  }, [startDate]);

  const validateEndTime = () => {
    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTimeObj = new Date();
      startTimeObj.setHours(startHour, startMinute);

      const endTimeObj = new Date();
      endTimeObj.setHours(endHour, endMinute);
    }
  };

 const formatTime = (time: string) => {
    if (time === "00:00") return "Midnight";
    return moment(time, "HH:mm").format("h:mm A");
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <ClockIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span
            className={`block xl:text-lg font-semibold ${
              searchBar === false ? "text-white" : "text-[#121212]"
            }`}
          >
            {`${startTime === "00:00" ? "Midnight" : startTime}`} -{" "}
            {`${endTime === "00:00" ? "Midnight" : endTime}`}
          </span>
          <span
            className={`block mt-1 text-sm  ${
              searchBar === false ? " text-white" : "text-[#121212]"
            } leading-none font-light`}
          >
            {"Pick up - Drop off"}
          </span>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (startDate && endDate) {
      const startDateTime = moment(
        `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
        "YYYY-MM-DD HH:mm"
      ).toDate();

      const endDateTime = moment(
        `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
        "YYYY-MM-DD HH:mm"
      ).toDate();

      setStartDate(startDateTime);
      setEndDate(endDateTime);
    }
  }, [startTime, endTime]);

  // Define time range type
  type TimeRange = {
    from: string; // HH:mm:ss
    to: string; // HH:mm:ss
  };

  type PartialUnavailability = {
    [date: string]: TimeRange[];
  };

  const getPartialUnavailableRanges = (
    date: Date | null | undefined
  ): TimeRange[] => {
    if (!date) return [];
    const m = moment(date);
    if (!m.isValid()) return [];
    const dateKey = m.format("YYYY-MM-DD");
    const ranges = partiallyUnavailableTimes[dateKey];
    return Array.isArray(ranges) ? ranges : [];
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string
  ): boolean => {
    if (!date) return false; // or `true` if you want to disable until a date is picked
    const ranges = getPartialUnavailableRanges(date);
    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Find the next available time from a given slot in your 30-min list
 const getNextAvailableTime = (
  date: Date | null | undefined,
  time: string,
  timeSlots: string[]
): string => {
  if (!date) return time;

  for (let i = timeSlots.indexOf(time); i < timeSlots.length; i++) {
    if (!isTimeBlocked(date, timeSlots[i])) return timeSlots[i];
  }

  for (let i = 0; i < timeSlots.length; i++) {
    if (!isTimeBlocked(date, timeSlots[i])) return timeSlots[i];
  }

  // Default to first slot if all are blocked
  return timeSlots[0];
};



  useEffect(() => {
    if (startDate && startTime && isTimeBlocked(startDate, startTime)) {
      setStartTime(getNextAvailableTime(startDate, startTime, timeSlots));
    }
    if (endDate && endTime && isTimeBlocked(endDate, endTime)) {
      setEndTime(getNextAvailableTime(endDate, endTime, timeSlots));
    }
  }, [startDate, endDate]);

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative flex ${className}`}
      >
        {({ open }) => (
          <>
            <div
              className={`flex-1 z-10  flex items-center focus:outline-none ${
                open ? "nc-hero-field-focused !rounded-xl" : ""
              }`}
            >
              <PopoverButton
                className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none `}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                {renderInput()}
              </PopoverButton>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute left-1/2 z-10 mt-3 top-full w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-l">
                <div className="p-4 space-x-2 overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-[#FFFFFF] dark:bg-[#121212]">
                  <div className="grid grid-cols-2">
                    <div>
                      <h3 className="text-lg font-medium flex justify-center">
                        Start Time
                      </h3>

                      <div className="overflow-y-auto h-48">
                        {timeSlots.map((time) => {
                          const isUnavailable = shouldBlockTime
                            ? isTimeBlocked(startDate ?? null, time)
                            : false;

                          return (
                            <div
                              key={time}
                              className={`cursor-pointer text-center py-2 px-4 mb-1 rounded-full transition ${
                                startTime === time
                                  ? "bg-primary-200 text-white"
                                  : ""
                              } ${
                                isUnavailable
                                  ? "opacity-50 cursor-not-allowed line-through"
                                  : ""
                              }`}
                              onClick={() => {
                                if (isUnavailable) {
                                  toast.error(
                                    "This time slot is unavailable due to a partial booking"
                                  );
                                  return;
                                }
                                setStartTime(time);
                              }}
                            >
                              {formatTime(time)}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium flex justify-center">
                        End Time
                      </h3>
                      <div className="overflow-y-auto h-48">
                        {timeSlots.map((time) => {
                          const isUnavailable = shouldBlockTime
                            ? isTimeBlocked(endDate ?? null, time)
                            : false;

                          return (
                            <div
                              key={time}
                              className={`cursor-pointer text-center py-2 px-4 hover:bg-primary-200 hover:text-white rounded-full ${
                                endTime === time
                                  ? "bg-primary-200 text-white"
                                  : ""
                              } ${
                                isUnavailable
                                  ? "opacity-50 cursor-not-allowed line-through"
                                  : ""
                              }`}
                              onClick={() => {
                                if (isUnavailable) {
                                  toast.error(
                                    "This time slot is unavailable due to a partial booking"
                                  );
                                  return;
                                }
                                setEndTime(time);
                              }}
                            >
                              {formatTime(time)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <PopoverButton className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-white font-semibold py-3 rounded-xl transition">
                    Save
                  </PopoverButton>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
      {location.pathname !== "/car-listing-detail" &&
        hasButtonSubmit &&
        location.pathname !== "/listing-car-detail" && (
          <div className="cursor-pointer min-w-30 mt-6 mb-0">
            {hasButtonSubmit && (
              <div className="flex justify-right md:pr-4">
                <div
                  onClick={() => {
                    if (pickupLocation === null) {
                      toast.error("Please enter Pick up location");
                    } else if (
                      dropOffLocation === null &&
                      dropOffLocationType === false
                    ) {
                      toast.error("Please enter Drop off location");
                    } else if (pickupLocation !== null) {
                      callAvailableFleetAPI(navigate);
                    }
                  }}
                  className={`h-14 md:h-16 w-full lg:w-16 rounded-md bg-primary-200 hover:bg-primary-700 lg:px-5 flex items-center justify-center text-text-color focus:outline-none ${
                    startDate && endDate ? "" : ""
                  }`}
                >
                  {loadingAvailableFleet ? (
                    <svg
                      aria-hidden="true"
                      className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  ) : (
                    <>
                      <span className="flex lg:hidden text-lg font-bold text-[#F9FAFB]">
                        Search
                      </span>
                      <span className="hidden lg:flex">
                        <div className="text-button-color">
                          <MagnifyingGlassIcon className="text-[#F9FAFB] w-6 h-6" />
                        </div>
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
    </>
  );
};

export default RentalCarTimesRangeInput;
